import { Link } from "react-router-dom";
import { useList } from "@refinedev/core";
import { AlertTriangle } from "lucide-react";
import type { AuthUser } from "@/hooks/useAuth";
import { DataCard } from "@/components/forms/DataCard";
import { MoneyDisplay } from "@/components/forms/MoneyDisplay";

interface Props {
  user: AuthUser;
}

export function DashboardAdmin({ user }: Props) {
  const hoy = new Date().toISOString().slice(0, 10);

  const { data: ventasHoy } = useList({
    resource: "ventas",
    filters: [
      { field: "fecha_venta", operator: "eq", value: hoy },
      { field: "estado", operator: "eq", value: "confirmada" },
    ],
    pagination: { mode: "off" },
  });

  const { data: porLiquidar } = useList({
    resource: "periodos_cobro",
    filters: [{ field: "estado", operator: "eq", value: "cerrado_tienda" }],
    pagination: { mode: "off" },
  });

  const { data: porPagar } = useList({
    resource: "periodos_cobro",
    filters: [{ field: "estado", operator: "eq", value: "liquidado" }],
    pagination: { mode: "off" },
  });

  const { data: anulacionesPendientes } = useList({
    resource: "anulaciones",
    filters: [{ field: "estado", operator: "eq", value: "devolucion_pendiente" }],
    pagination: { mode: "off" },
  });

  const totalVentasHoy = ventasHoy?.data.reduce((s, v) => s + (v.valor_total as number), 0) ?? 0;
  const totalPorLiquidar = porLiquidar?.data.reduce(
    (s, p) => s + (p.total_ventas as number),
    0,
  ) ?? 0;
  const totalPorPagar = porPagar?.data.reduce(
    (s, p) => s + (p.valor_final_transferencia as number),
    0,
  ) ?? 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold">Hola, {user.nombre_completo.split(" ")[0]}</h1>
        <p className="text-fg-muted">Panel de control central</p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <DataCard title="Ventas hoy" subtitle={`${ventasHoy?.data.length ?? 0} ventas`}>
          <MoneyDisplay value={totalVentasHoy} className="text-2xl font-bold" />
        </DataCard>

        <DataCard
          title="Por liquidar"
          subtitle={`${porLiquidar?.data.length ?? 0} cortes`}
          footer={
            <Link to="/admin/kanban" className="text-sm text-brand hover:underline">
              Ir al kanban →
            </Link>
          }
        >
          <MoneyDisplay value={totalPorLiquidar} className="text-2xl font-bold text-state-cerrado" />
        </DataCard>

        <DataCard
          title="Por pagar"
          subtitle={`${porPagar?.data.length ?? 0} cortes liquidados`}
          footer={
            <Link to="/pagos/nuevo" className="text-sm text-brand hover:underline">
              Registrar pago →
            </Link>
          }
        >
          <MoneyDisplay value={totalPorPagar} className="text-2xl font-bold text-state-liquidado" />
        </DataCard>

        <DataCard
          title="Devoluciones pendientes"
          subtitle="Esperando que la financiera devuelva"
          footer={
            <Link to="/anulaciones" className="text-sm text-brand hover:underline">
              Ver anulaciones →
            </Link>
          }
        >
          <p className="text-2xl font-bold text-warning">
            {anulacionesPendientes?.data.length ?? 0}
          </p>
        </DataCard>
      </div>

      {(porLiquidar?.data.length ?? 0) > 0 && (
        <section className="card p-4 bg-warning/5 border-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="size-5 text-warning shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="font-semibold">Necesita tu atención</h2>
              <p className="text-sm text-fg-secondary">
                Hay {porLiquidar?.data.length} corte(s) esperando liquidación.
              </p>
              <Link
                to="/admin/kanban"
                className="inline-block mt-2 text-sm text-brand hover:underline"
              >
                Ir a liquidar →
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
